import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchAPI } from "@/lib/api";
import { Footer } from "@/components/layout/Footer";
import { Calendar, Clock, ArrowLeft, Tag, Share2, Play } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface Story {
  id: number;
  circuit_id: number;
  circuit_title?: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image_url: string;
  category: string;
  read_time: string;
  published_date: string;
  featured: number;
  instagram_reel_url?: string; // Repurposed for YouTube Short URL
}

const getYoutubeVideoId = (url: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  if (match && match[2].length === 11) {
      return match[2];
  }
  
  // Handle shorts specifically if not covered above or generic shorts link
  if (url.includes("/shorts/")) {
      const parts = url.split("/shorts/");
      return parts[1]?.split("?")[0]; 
  }

  return null;
}

import { useToast } from "@/components/ui/use-toast";

// ... existing imports

const StoryDetail = () => {
  const { slug } = useParams();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: story?.title,
          text: story?.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link Copied",
        description: "Story link copied to clipboard",
      });
    }
  };

  useEffect(() => {
    if (slug) {
      fetchAPI(`?type=stories&slug=${slug}`)
        .then((data: any) => {
          if (data && data.id) {
            setStory(data);
          } else {
            setError("Story not found");
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching story:", err);
          setError("Failed to load story");
          setLoading(false);
        });
    }
  }, [slug]);

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="animate-pulse flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                <p className="text-muted-foreground font-serif">Loading Story...</p>
            </div>
        </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background">
        <h1 className="text-3xl font-serif text-destructive">Story Not Found</h1>
        <p className="text-muted-foreground">We couldn't find the story you're looking for.</p>
        <Link to="/stories">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4"/> Back to Stories
          </Button>
        </Link>
      </div>
    );
  }

  const videoId = story.instagram_reel_url ? getYoutubeVideoId(story.instagram_reel_url) : null;

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-orange-100 selection:text-orange-900">
      <SEO 
        title={story.title} 
        description={story.excerpt}
        image={`https://baelleaves.com/${story.image_url}`}
        canonical={`https://baelleaves.com/stories/${story.slug}`}
      />
      
      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] w-full overflow-hidden">
        <motion.div 
            initial={{ scale: 1.1, opacity: 0.8 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0"
        >
            <img 
            src={`https://baelleaves.com/${story.image_url}`} 
            alt={story.title} 
            className="w-full h-full object-cover"
            loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background" />
        </motion.div>

        <div className="absolute inset-0 flex items-center justify-center">
            <div className="container mx-auto px-6 pt-20">
                <motion.div 
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                    className="max-w-4xl mx-auto text-center"
                >
                    <span className="inline-block py-1 px-4 mb-6 rounded-full bg-orange-500/90 text-white text-sm font-medium tracking-wide shadow-lg backdrop-blur-sm">
                        {story.category}
                    </span>
                    <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-[1.1] drop-shadow-xl">
                        {story.title}
                    </h1>
                    {/* Date and Read Time removed from here */}
                </motion.div>
            </div>
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-8 -mt-20 relative z-10 pb-20">
        <div className={`grid grid-cols-1 ${videoId ? 'lg:grid-cols-[1fr_380px]' : 'max-w-4xl mx-auto'} gap-12`}>
            
            {/* Main Content */}
            <motion.div 
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="bg-card rounded-2xl p-8 md:p-12 shadow-xl border border-border/50"
            >
                {/* Back Link */}
                <Link to="/stories" className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors mb-10 group">
                    <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> 
                    Back to all stories
                </Link>

                {/* Excerpt */}
                <div className="mb-12">
                    <p className="text-xl md:text-2xl font-serif text-foreground/80 leading-relaxed border-l-4 border-orange-500 pl-6 italic">
                        {story.excerpt}
                    </p>
                </div>

                {/* Content */}
                <div className="prose prose-lg md:prose-xl dark:prose-invert max-w-none font-serif leading-8 text-foreground/90 whitespace-pre-wrap first-letter:text-5xl first-letter:font-bold first-letter:text-orange-600 first-letter:mr-3 first-letter:float-left">
                    {story.content}
                </div>

                {/* Footer Meta */}
                <div className="mt-16 pt-8 border-t border-border flex flex-wrap gap-4 items-center justify-between">
                     <div className="flex items-center gap-3 text-muted-foreground bg-secondary/50 px-4 py-2 rounded-lg">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-orange-500" />
                                <span className="font-medium text-foreground">{story.published_date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-orange-500" />
                                <span className="font-medium text-foreground">{story.read_time}</span>
                            </div>
                        </div>
                     </div>
                     <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-2 text-muted-foreground hover:text-foreground"
                        onClick={handleShare}
                     >
                        <Share2 className="w-4 h-4" /> Share Story
                     </Button>
                </div>
            </motion.div>

            {/* Sidebar / Aside */}
            {videoId && (
                <motion.aside 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                    className="space-y-8 h-fit lg:pt-0"
                >
                    {/* YouTube Short Card */}
                    <div className="group sticky top-24">
                            <div className="bg-card rounded-2xl overflow-hidden shadow-2xl border border-border hover:shadow-orange-500/10 transition-shadow duration-300">
                            <div className="p-4 border-b border-border bg-gradient-to-r from-orange-50 to-orange-100/50 dark:from-orange-950/20 dark:to-background">
                                <h3 className="font-serif text-lg font-bold flex items-center gap-3 text-orange-900 dark:text-orange-50">
                                        <span className="relative flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                    </span>
                                    Related Short
                                </h3>
                            </div>
                            <div className="relative aspect-[9/16] w-full bg-black">
                                <iframe 
                                    className="absolute inset-0 w-full h-full"
                                    src={`https://www.youtube.com/embed/${videoId}?autoplay=0&controls=1&rel=0&loop=1`}
                                    title="YouTube video player"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    loading="lazy"
                                ></iframe>
                            </div>
                            <div className="p-4 bg-card">
                                <Button className="w-full gap-2" variant="secondary" asChild>
                                    <a href={story.instagram_reel_url} target="_blank" rel="noopener noreferrer">
                                        <Play className="w-4 h-4 fill-current" /> Watch on YouTube
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.aside>
            )}
            
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StoryDetail;
